import { AssignDeckDTO, CreateDeckDTO, CreateDeckExerciseDTO, DeleteDeckDTO, RemoveDeckExerciseDTO, UpdateDeckDTO, UpdateDeckExerciseDTO } from '@/generated';

import { deckApi } from '@/generated/clients';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './query-hooks';

export const useDeleteDeckMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deleteDeckDTO: DeleteDeckDTO) => deckApi.deckDeleteDeck({ deleteDeckDTO }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.Decks] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.AllDecks] });
    },
  });
};

export const useAssignDeckToUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignDeckDTO: AssignDeckDTO) => deckApi.deckAssignDeckToUser({ assignDeckDTO }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.Decks] });
    },
  });
};

export const useUnassignDeckFromUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignDeckDTO: AssignDeckDTO) => deckApi.deckUnassignDeckFromUser({ assignDeckDTO }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.Decks] });
    },
  });
};

export const useUpdateDeck = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updateDeckDTO: UpdateDeckDTO) => deckApi.deckUpdateDeck({ updateDeckDTO }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.Deck] });
    },
  });
};

export const useCreateDeck = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (createDeckDTO: CreateDeckDTO) => deckApi.deckCreateDeck({ createDeckDTO }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.Decks] });
    },
  });
};

export const useAddExerciseToDeck = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (createDeckExerciseDTO: CreateDeckExerciseDTO) => deckApi.deckAddExerciseToDeck({ createDeckExerciseDTO }),
    onSuccess: (_, dto) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.DeckExercises, dto.deckId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.DeckValidation, dto.deckId] });
    },
  });
};

export const useUpdateExerciseInDeck = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updateDeckExerciseDTO: UpdateDeckExerciseDTO) => deckApi.deckUpdateDeckExercise({ updateDeckExerciseDTO }),
    onSuccess: (_, dto) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.DeckExercises, dto.deckId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.DeckValidation, dto.deckId] });
    },
  });
};

export const useRemoveExerciseInDeck = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (removeDeckExerciseDTO: RemoveDeckExerciseDTO) => deckApi.deckRemoveDeckExercise({ removeDeckExerciseDTO }),
    onSuccess: (_, dto) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.DeckExercises, dto.deckId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.DeckValidation, dto.deckId] });
    },
  });
};
